// Instagram API with Instagram Login
// Direct Instagram authentication for business accounts

const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;
const INSTAGRAM_APP_SECRET = process.env.REACT_APP_INSTAGRAM_APP_SECRET;
const INSTAGRAM_REDIRECT_URI = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI;

// Backend API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';

// Scopes for Instagram business functionality
const INSTAGRAM_SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_messages', 
  'instagram_business_manage_comments',
  'instagram_business_content_publish',
  'instagram_business_manage_insights'
].join(',');

// Step 1: Redirect user to Instagram authorization
export const initiateInstagramLogin = () => {
  const authUrl = new URL('https://www.instagram.com/oauth/authorize');
  authUrl.searchParams.append('force_reauth', 'true');
  authUrl.searchParams.append('client_id', INSTAGRAM_APP_ID);
  authUrl.searchParams.append('redirect_uri', INSTAGRAM_REDIRECT_URI);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('scope', INSTAGRAM_SCOPES);

  console.log('Instagram Login: Redirecting to authorization URL:', authUrl.toString());
  
  // Redirect to Instagram authorization
  window.location.href = authUrl.toString();
};

// Step 2: Exchange authorization code for access token (via backend)
export const exchangeCodeForToken = async (code) => {
  try {
    console.log('Instagram Login: Exchanging code for token via backend');
    
    const response = await fetch(`${API_BASE_URL}/api/instagram/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Instagram Login: Token exchange successful via backend');
    
    return data.userData;
  } catch (error) {
    console.error('Instagram Login: Token exchange error:', error);
    throw error;
  }
};

// Step 3: Get long-lived access token
export const getLongLivedToken = async (shortLivedToken) => {
  try {
    console.log('Instagram Login: Getting long-lived token');
    
    const url = new URL('https://graph.instagram.com/access_token');
    url.searchParams.append('grant_type', 'ig_exchange_token');
    url.searchParams.append('client_secret', INSTAGRAM_APP_SECRET);
    url.searchParams.append('access_token', shortLivedToken);

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`Long-lived token request failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Instagram Login: Long-lived token obtained', data);
    
    return {
      accessToken: data.access_token,
      tokenType: data.token_type,
      expiresIn: data.expires_in
    };
  } catch (error) {
    console.error('Instagram Login: Long-lived token error:', error);
    throw error;
  }
};

// Step 4: Get user profile information
export const getUserProfile = async (accessToken) => {
  try {
    console.log('Instagram Login: Getting user profile');
    
    const fields = [
      'id',
      'username', 
      'name',
      'account_type',
      'profile_picture_url',
      'followers_count',
      'follows_count',
      'media_count'
    ].join(',');

    const url = new URL('https://graph.instagram.com/me');
    url.searchParams.append('fields', fields);
    url.searchParams.append('access_token', accessToken);

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`User profile request failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Instagram Login: User profile obtained', data);
    
    return {
      id: data.id,
      username: data.username,
      name: data.name,
      accountType: data.account_type,
      profilePictureUrl: data.profile_picture_url,
      followersCount: data.followers_count,
      followsCount: data.follows_count,
      mediaCount: data.media_count
    };
  } catch (error) {
    console.error('Instagram Login: User profile error:', error);
    throw error;
  }
};

// Complete Instagram login flow (now handled by backend)
export const handleInstagramLoginFlow = async (code) => {
  try {
    console.log('Instagram Login: Starting complete login flow via backend');
    
    // Backend handles all steps: code exchange, long-lived token, and user profile
    const userData = await exchangeCodeForToken(code);
    
    console.log('Instagram Login: Complete login flow successful', userData);
    
    // Store in localStorage
    localStorage.setItem('instagramUser', JSON.stringify(userData));
    localStorage.setItem('instagramAccessToken', userData.accessToken);
    
    return userData;
  } catch (error) {
    console.error('Instagram Login: Complete flow error:', error);
    throw error;
  }
};

// Check if user is logged in via Instagram
export const getInstagramUser = () => {
  try {
    const userData = localStorage.getItem('instagramUser');
    const accessToken = localStorage.getItem('instagramAccessToken');
    
    if (userData && accessToken) {
      const user = JSON.parse(userData);
      
      // Check if token is still valid
      if (user.tokenExpiry && Date.now() < user.tokenExpiry) {
        return user;
      } else {
        // Token expired, clear storage
        localStorage.removeItem('instagramUser');
        localStorage.removeItem('instagramAccessToken');
        return null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Instagram Login: Get user error:', error);
    return null;
  }
};

// Logout user
export const logoutInstagramUser = () => {
  console.log('Instagram Login: Logging out user');
  localStorage.removeItem('instagramUser');
  localStorage.removeItem('instagramAccessToken');
};

// Refresh long-lived token (call this periodically)
export const refreshInstagramToken = async () => {
  try {
    const userData = getInstagramUser();
    if (!userData || !userData.accessToken) {
      throw new Error('No user or access token found');
    }
    
    console.log('Instagram Login: Refreshing token');
    
    const url = new URL('https://graph.instagram.com/refresh_access_token');
    url.searchParams.append('grant_type', 'ig_refresh_token');
    url.searchParams.append('access_token', userData.accessToken);
    
    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Update stored data
    userData.accessToken = data.access_token;
    userData.tokenExpiry = Date.now() + (data.expires_in * 1000);
    
    localStorage.setItem('instagramUser', JSON.stringify(userData));
    localStorage.setItem('instagramAccessToken', data.access_token);
    
    console.log('Instagram Login: Token refreshed successfully');
    return data.access_token;
  } catch (error) {
    console.error('Instagram Login: Token refresh error:', error);
    throw error;
  }
};
